import { useEffect, useState } from "react";

import { queryClient } from "@/lib/query-client";
import { authService } from "@/services/api";

import { AuthContext, AuthContextType, LoginData } from "./AuthContext";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem("verden-token");
    return storedToken ?? null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<number | null>(() => {
    const storedUserId = localStorage.getItem("verden-user-id");
    return storedUserId ? parseInt(storedUserId, 10) : null;
  });
  const [userName, setUserName] = useState<string>(() => {
    const storedUserName = localStorage.getItem("verden-user-name");
    return storedUserName ?? "";
  });

  const isAuthenticated = !!token;

  useEffect(() => {
    const checkAuth = async () => {
      const tokenFromStorage = localStorage.getItem("verden-token");
      setToken(tokenFromStorage);

      const userIdStorage = localStorage.getItem("verden-user-id");
      if (userIdStorage) {
        setUserId(parseInt(userIdStorage, 10));
      }

      const userNameStorage = localStorage.getItem("verden-user-name");
      if (userNameStorage) {
        setUserName(userNameStorage);
      }

      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = (data: LoginData) => {
    localStorage.setItem("verden-token", data.access_token);
    localStorage.setItem("verden-user-id", data.user_id.toString());
    localStorage.setItem("verden-client-id", data.client_id.toString());
    localStorage.setItem("verden-user-name", data.user_name ?? "");
    setToken(data.access_token);
    setUserId(data.user_id);
    setUserName(data.user_name ?? "");
  };

  const clearSession = () => {
    localStorage.removeItem("verden-token");
    // Chave de sessões antigas, de quando o login gravava um refresh token que a API nunca enviou.
    localStorage.removeItem("verden-refresh-token");
    localStorage.removeItem("verden-user-id");
    localStorage.removeItem("verden-client-id");
    localStorage.removeItem("verden-user-name");
    setToken(null);
    setUserId(null);
    setUserName("");
    // O login seguinte acontece na mesma instancia do QueryClient: sem limpar o
    // cache, um novo ator com o mesmo id reaproveita os dados do anterior.
    queryClient.clear();
  };

  const logout = async () => {
    try {
      // Revoga os tokens no servidor enquanto o Authorization local ainda existe.
      await authService.logout();
    } catch {
      // Se o token ja estiver invalido no servidor, a sessao local sai do mesmo jeito.
    }

    clearSession();
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    token,
    userId,
    userName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
